import React from 'react';
import { Outlet, NavLink, Navigate } from 'react-router-dom';
import { LayoutDashboard, Users, Activity, LogOut, Shield } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useWorkspace } from '@/hooks/useWorkspace';

export default function SuperAdminLayout() {
    const { user, signOut } = useAuth();
    const { isSuperAdmin } = useWorkspace();

    // Double protection: check both email and super admin flag
    if (!user || !isSuperAdmin) {
        return <Navigate to="/dashboard" replace />;
    }

    const handleSignOut = async () => {
        await signOut();
    };

    return (
        <div className="flex h-dvh bg-background overflow-hidden selection:bg-sidebar-accent selection:text-sidebar-accent-foreground">

            {/* Admin Sidebar */}
            <aside className="w-64 bg-zinc-900 text-zinc-100 flex flex-col border-r border-zinc-800">
                <div className="p-6">
                    <div className="flex items-center gap-2">
                        <Shield className="h-5 w-5 text-amber-500" />
                        <h1 className="text-xl font-bold tracking-tight">Admin Dashboard</h1>
                    </div>
                    <span className="text-xs text-zinc-400 uppercase tracking-wider">Super Admin Panel</span>
                </div>

                <nav className="flex-1 px-4 space-y-2 mt-4">
                    <NavItem to="/admin" icon={<LayoutDashboard size={20} />} label="Overview" end />
                    <NavItem to="/admin/users" icon={<Users size={20} />} label="User Management" />
                    <NavItem to="/admin/health" icon={<Activity size={20} />} label="System Health" />
                </nav>

                <div className="p-4 border-t border-zinc-800">
                    <NavLink
                        to="/dashboard"
                        className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-zinc-400 hover:text-zinc-100 transition-colors w-full rounded-lg hover:bg-zinc-800"
                    >
                        <LayoutDashboard size={18} />
                        Back to App
                    </NavLink>
                    <button
                        onClick={handleSignOut}
                        className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-zinc-400 hover:text-zinc-100 transition-colors w-full rounded-lg hover:bg-zinc-800"
                    >
                        <LogOut size={18} />
                        Sign Out
                    </button>
                </div>
            </aside>

            {/* Main Content Area */}
            <main className="flex-1 overflow-y-auto bg-zinc-950 p-8">
                <div className="max-w-7xl mx-auto">
                    <Outlet />
                </div>
            </main>
        </div>
    );
};

const NavItem = ({ to, icon, label, end = false }: { to: string; icon: React.ReactNode; label: string; end?: boolean }) => (
    <NavLink
        to={to}
        end={end}
        className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-[background-color,color,box-shadow] ${isActive
                ? 'bg-amber-600 text-white shadow-lg shadow-amber-900/30'
                : 'text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100'
            }`
        }
    >
        {icon}
        {label}
    </NavLink>
);


